import React from 'react';
import PropTypes from 'prop-types';
import i18n from '@wix/wix-vod-shared/src/common/i18n';
import * as dealInfoHelpers from '@wix/wix-vod-shared/src/common/utils/deal-info-helpers';
import defaultSettings from '@wix/wix-vod-shared/src/app-settings/default-settings';
import autobind from 'core-decorators/lib/autobind';
import { notForPreview } from 'widget/utils/not-for-preview';
import { convertSToMS } from '@wix/wix-vod-shared/src/widget/utils/time';
import { openFullScreenSubscribeOverlay, openFullScreenPurchaseOverlay } from 'shared/utils/open-overlay';

import {
  getBuyButtonDisplayTypeWhenInOverlay,
  shouldShowChannelCover,
} from '@wix/wix-vod-shared/src/common/utils/overlay-buttons-utils';
import { getPlayButtonTypeString } from 'shared/utils/bi/video-overlay-events-params';
import { isPreviewOrEditorMode, openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';
import { canAccessFullVideo } from '@wix/wix-vod-shared/src/widget/ui-selectors/video-access';
import { withBiEvent } from 'shared/bi/send-bi-event';
import bi from 'shared/bi/bi';

import renderVideoPlayButtonText from 'widget/components/overlay-texts/video/play-button-text';
import renderVideoSignInToWatchButtonText from 'widget/components/overlay-texts/video/sign-in-to-watch-button-text';
import renderVideoSubscribeButtonText from 'widget/components/overlay-texts/video/subscribe-button-text';
import renderChannelPlayButtonText from 'widget/components/overlay-texts/channel/play-button-text';
import renderChannelSignInToWatchButtonText from 'widget/components/overlay-texts/channel/sign-in-to-watch-button-text';
import renderChannelSubscribeButtonText from 'widget/components/overlay-texts/channel/subscribe-button-text';
import renderOpenVideoWithoutPreviewButtonText from 'widget/components/overlay-texts/channel/open-video-without-preview-button-text';
import renderPurchaseButtonText from 'widget/components/overlay-texts/purchase-button-text';

import sharedTranslations from 'messages/shared';


export default class BaseOverlay extends React.Component {

  static propTypes = {
    channelData: PropTypes.object,
    videoItem: PropTypes.object,
    appSettings: PropTypes.object.isRequired,
    currentSiteUser: PropTypes.object,
    showChannelCover: PropTypes.bool,
    beenPlayed: PropTypes.bool,
    ended: PropTypes.bool,
    className: PropTypes.string,
    onPlayClick: PropTypes.func,
    playSelectedVideo: PropTypes.func,
    playVideoItem: PropTypes.func, // comes dome player
    playNextVideo: PropTypes.func,
    nextVideoId: PropTypes.string,
    canShowMainItemPublisher: PropTypes.bool,
    canShowMainItemTitle: PropTypes.bool,
  };

  static defaultProps = {
    beenPlayed: false,
    ended: false,
    showChannelCover: false,
    onPlayClick: _.noop,
    playSelectedVideo: _.noop,
    playNextVideo: _.noop,
    biEvent: _.noop,
  };

  componentDidMount() {
    const { videoItem } = this.props;

    if (videoItem && this.subscribeButtonText) {
      this.sendSubscribtionDisplayedBI();
    }
  }

  get subscriptionPriceInfo() {
    return dealInfoHelpers.getSubscriptionPriceInfo(this.props.channelData.dealInfo);
  }

  get minVideoPriceInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getMinPriceInfo(videoItem.dealInfo);
  }

  get saleDealInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getSalePriceInfo(videoItem.dealInfo);
  }

  get rentDealInfo() {
    const { videoItem } = this.props;
    if (!videoItem) {
      return null;
    }
    return dealInfoHelpers.getRentPriceInfo(videoItem.dealInfo);
  }

  get hasNextVideo() {
    const { nextVideoId } = this.props;
    return Boolean(nextVideoId);
  }

  get titleText() {
    const { channelData, videoItem } = this.props;
    return this.isChannelCoverView ? channelData.title : videoItem.title;
  }

  get shouldShowPublisher() {
    const { videoItem: { publisher }, canShowMainItemPublisher } = this.props;
    return canShowMainItemPublisher && publisher && !this.isChannelCoverView;
  }

  get shouldShowTitle() {
    return this.props.canShowMainItemTitle && this.titleText;
  }


  get isChannelCoverView() {
    const { appSettings, showChannelCover } = this.props;
    // Todo: move to connect
    return shouldShowChannelCover(appSettings, showChannelCover);
  }

  get buttonTextProps() {
    const { channelData, videoItem, currentSiteUser } = this.props;

    return {
      channel: channelData,
      video: videoItem,
      currentSiteUser,
    };
  }

  get playButtonText() {
    const props = this.buttonTextProps;

    // Todo: move each button type to separate methods and use at views
    if (this.isChannelCoverView) {
      return renderChannelPlayButtonText(props) ||
        renderChannelSignInToWatchButtonText(props) ||
        renderOpenVideoWithoutPreviewButtonText(props);
    }
    return renderVideoPlayButtonText(props) || renderVideoSignInToWatchButtonText(props);
  }

  get playNextButtonText() {
    const { ended } = this.props;

    if (!ended || this.minVideoPriceInfo || !this.hasNextVideo) {
      return null;
    }

    return i18n.t(sharedTranslations.nextVideo);
  }

  get subscribeButtonText() {
    // Todo: move each button type to separate methods and use at views

    const props = this.buttonTextProps;
    if (this.isChannelCoverView) {
      return renderChannelSubscribeButtonText(props);
    }

    return renderVideoSubscribeButtonText(props);
  }

  get purchaseButtonText() {
    if (this.isChannelCoverView) {
      return null;
    }
    return renderPurchaseButtonText(this.buttonTextProps);
  }

  sendSubscribtionDisplayedBI() {
    bi.log('widget.subscription.displayed', { whereDisplayed: 'cover' });
  }

  sendPlayButtonBI() {
    const { channelData, videoItem, currentSiteUser } = this.props;
    if (this.isChannelCoverView) {
      const button = canAccessFullVideo(channelData, videoItem, currentSiteUser) ?
        'startWatching' :
        'watchPreview';

      this.sendChCoverButtonClickedBI(button);
    } else {
      this.sendVideoButtonClickedBI(getPlayButtonTypeString(channelData, videoItem, currentSiteUser));
    }

    const { videoSource } = videoItem;
    bi.log('widget.vid.clicked', {
      duration: convertSToMS(videoItem.durationSec),
      channelID: channelData.id,
      videoType: videoSource,
    });
  }

  // TODO: this component should call prop onPlayClick without additional logic. Move to view
  @autobind
  playSelectedVideo(event, shouldSendPlayStartBi = true) {
    event.preventDefault();
    event.stopPropagation();

    const { playSelectedVideo, videoItem } = this.props;

    if (isPreviewOrEditorMode() && videoItem.memberOnly) {
      openPreviewPrompt();
      return;
    }

    if (shouldSendPlayStartBi) {
      this.sendPlayButtonBI();
    }

    playSelectedVideo(videoItem);
  }

  @autobind
  playNextVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    this.props.playNextVideo();
  }

  sendSubscribeClickedBI() {
    this.sendChCoverButtonClickedBI('subscribe');
  }

  @autobind
  @withBiEvent('widget.subscription.clicked')
  @notForPreview
  openSubscription(event) {
    event.preventDefault();
    event.stopPropagation();
    this.sendSubscribeClickedBI();

    const { channelData } = this.props;

    openFullScreenSubscribeOverlay(channelData.id, () => {
      bi.log('widget.subscriptionPopUp.Completed', { result: 'canceled', errorMsg: '' });
    });
  }

  @autobind
  @notForPreview
  openPurchase(event) {
    event.preventDefault();
    event.stopPropagation();
    this.sendOpenPurchaseBI();
    const { channelData, videoItem } = this.props;

    openFullScreenPurchaseOverlay(channelData.id, videoItem.id);
  }

  sendOpenPurchaseBI() {
    const { appSettings, videoItem, channelData, showChannelCover } = this.props;

    const buttonDisplayType = getBuyButtonDisplayTypeWhenInOverlay({
      settings: appSettings,
      videoItem,
      channelData,
      showChannelCover,
    });
    this.sendVideoButtonClickedBI(buttonDisplayType);
  }

  sendChCoverButtonClickedBI(button) {
    bi.log('widget.chCover.btn.clicked', { button });
  }

  sendVideoButtonClickedBI(button) {
    const { videoItem } = this.props;

    bi.log('widget.VidSelected.btn.clicked', {
      button,
      buttonType: 'main',
      videoID: videoItem.id,
    });
  }

  getOpacityStyles() {
    const { appSettings } = this.props;
    const mainItemOpacity = _.get(appSettings, 'numbers.mainItemOverlayOpacity');

    const opacityPercents = _.isNumber(mainItemOpacity) ?
      mainItemOpacity :
      _.get(defaultSettings, 'numbers.mainItemOverlayOpacity');
    const opacity = _.round(opacityPercents / 100, 2);

    return {
      backgroundColor: `rgba(0,0,0,${opacity})`,
    };
  }
}



// WEBPACK FOOTER //
// ./src/widget/components/player-overlay/_base-overlay.js